import React, { useState, useEffect, useRef } from 'react';
import { X, Share2, Link2, User, QrCode } from 'lucide-react';
import QRCode from 'react-qr-code';
import { useToast } from '../hooks/use-toast';

/**
 * Enhanced QR Popup Component
 * Features:
 * - Bio URL QR Code (default view)
 * - Offline vCard QR Code (works without internet)
 * - Single Share button
 */

// Generate simplified vCard with only essential fields
const generateSimpleVCard = (card) => {
  const escapeVCard = (text) => {
    if (!text) return '';
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  };

  let vCardLines = [];
  vCardLines.push('BEGIN:VCARD');
  vCardLines.push('VERSION:3.0');
  
  // Full Name
  if (card.name) {
    vCardLines.push(`FN:${escapeVCard(card.name)}`);
    const nameParts = card.name.trim().split(' ');
    if (nameParts.length > 1) {
      const lastName = nameParts[nameParts.length - 1];
      const firstName = nameParts.slice(0, -1).join(' ');
      vCardLines.push(`N:${escapeVCard(lastName)};${escapeVCard(firstName)};;;`);
    } else {
      vCardLines.push(`N:${escapeVCard(card.name)};;;;`);
    }
  }
  
  // Company
  if (card.company) {
    vCardLines.push(`ORG:${escapeVCard(card.company)}`);
  }
  
  // Designation/Title
  if (card.title) {
    vCardLines.push(`TITLE:${escapeVCard(card.title)}`);
  }
  
  // Phone
  if (card.phone) {
    const cleanPhone = card.phone.replace(/[^\d+]/g, '');
    vCardLines.push(`TEL;TYPE=CELL:${cleanPhone}`);
  }
  
  // Email
  if (card.email) {
    vCardLines.push(`EMAIL:${card.email}`);
  }
  
  vCardLines.push('END:VCARD');
  return vCardLines.join('\r\n');
};

const QRPopupEnhanced = ({ 
  isOpen, 
  onClose, 
  card, 
  bioUrl, 
  qrColor = '#4F46E5',
  logoSrc = '/logo.png',
  isPremium = false,
  onQRDownload
}) => {
  const [activeTab, setActiveTab] = useState('url'); // 'url' or 'contact'
  const [vCardData, setVCardData] = useState('');
  const [isGeneratingVCard, setIsGeneratingVCard] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const { toast } = useToast();
  const modalRef = useRef(null);
  
  // Swipe detection refs
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const minSwipeDistance = 50;

  // Handle tab switch with rotation animation
  const handleTabSwitch = (tab) => {
    if (tab !== activeTab) {
      setIsRotating(true);
      setTimeout(() => {
        setActiveTab(tab);
        setTimeout(() => setIsRotating(false), 300);
      }, 150);
    }
  };

  // Swipe handlers for left/right navigation
  const handleTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isSwipeLeft = distance > minSwipeDistance;
    const isSwipeRight = distance < -minSwipeDistance;
    
    if (isSwipeLeft && activeTab === 'url') {
      // Swipe left: go to Offline Contact
      handleTabSwitch('contact');
    } else if (isSwipeRight && activeTab === 'contact') {
      // Swipe right: go to Bio Link
      handleTabSwitch('url');
    }
    
    // Reset
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Generate simplified vCard data when contact tab is selected
  useEffect(() => {
    const generateVCardData = async () => {
      if (activeTab === 'contact' && card && !vCardData) {
        setIsGeneratingVCard(true);
        try {
          // Generate simplified vCard with only essential fields
          const vCard = generateSimpleVCard(card);
          setVCardData(vCard);
        } catch (error) {
          console.error('Error generating vCard:', error);
          toast({
            title: 'Error',
            description: 'Failed to generate contact QR code',
            variant: 'destructive'
          });
        } finally {
          setIsGeneratingVCard(false);
        }
      }
    };

    generateVCardData();
  }, [activeTab, card, vCardData, toast]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Share QR Code Image
  const handleShareQRImage = async () => {
    try {
      toast({
        title: 'Preparing QR...',
        description: 'Please wait.'
      });

      const qrElementId = activeTab === 'url' ? 'qr-popup-url' : 'qr-popup-contact';
      const qrElement = document.getElementById(qrElementId);
      
      if (!qrElement) {
        throw new Error('QR code not found');
      }

      // Try html-to-image first
      let dataUrl;
      try {
        const { toPng } = await import('html-to-image');
        dataUrl = await toPng(qrElement, {
          quality: 1,
          pixelRatio: 4,
          backgroundColor: '#ffffff',
          skipFonts: true,
          cacheBust: true
        });
      } catch (htmlToImageError) {
        console.error('html-to-image failed:', htmlToImageError);
        
        // Fallback: Canvas capture
        const svgElement = qrElement.querySelector('svg');
        if (!svgElement) throw new Error('QR SVG not found');

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);

        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = svgUrl;
        });

        canvas.width = 512;
        canvas.height = 512;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        URL.revokeObjectURL(svgUrl);
        dataUrl = canvas.toDataURL('image/png');
      }

      // Convert to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], `${card?.name || 'QR'}_${activeTab === 'url' ? 'Bio' : 'Contact'}.png`, { type: 'image/png' });

      // Try Web Share API for mobile
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `${card?.name || 'Contact'}'s QR Code`,
          text: activeTab === 'url' 
            ? `Scan to view ${card?.name || 'Contact'}'s digital card` 
            : `Scan to save ${card?.name || 'Contact'}'s contact info`,
          files: [file],
          url: activeTab === 'url' ? bioUrl : undefined
        });
        toast({
          title: 'Shared!',
          description: 'QR code shared successfully.'
        });
      } else {
        // Fallback: Download
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast({
          title: 'Downloaded!',
          description: 'QR code saved to your device.'
        });
      }

      // Track download if callback provided
      if (onQRDownload) {
        onQRDownload();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing QR:', error);
        toast({
          title: 'Error',
          description: 'Failed to share QR code.',
          variant: 'destructive'
        });
      }
    }
  };

  const currentQRValue = activeTab === 'url' ? bioUrl : vCardData;

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center"
      data-testid="qr-popup-overlay"
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm overflow-hidden animate-slide-up sm:animate-none"
        onClick={(e) => e.stopPropagation()}
        data-testid="qr-popup-modal"
      >
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5" />
            <h3 className="font-bold text-lg">QR Code</h3>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-white/20 rounded-full p-1.5 transition-all"
            data-testid="qr-popup-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => handleTabSwitch('url')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'url'
                ? 'text-amber-600 border-b-2 border-amber-500 bg-amber-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="qr-tab-url"
          >
            <Link2 className="w-4 h-4" />
            <span>Bio Link</span>
          </button>
          <button
            onClick={() => handleTabSwitch('contact')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'contact'
                ? 'text-amber-600 border-b-2 border-amber-500 bg-amber-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="qr-tab-contact"
          >
            <User className="w-4 h-4" />
            <span>Offline Contact</span>
          </button>
        </div>

        {/* QR Code Display - Swipeable area */}
        <div 
          className="p-6 flex flex-col items-center"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Tab Description */}
          <p className="text-xs text-gray-500 mb-4 text-center">
            {activeTab === 'url' 
              ? 'Scan to open the digital card in browser'
              : 'Scan to save contact directly (works offline)'}
          </p>

          {/* Swipe indicator dots */}
          <div className="flex gap-2 mb-3">
            <div className={`w-2 h-2 rounded-full transition-colors ${activeTab === 'url' ? 'bg-amber-500' : 'bg-gray-300'}`}></div>
            <div className={`w-2 h-2 rounded-full transition-colors ${activeTab === 'contact' ? 'bg-amber-500' : 'bg-gray-300'}`}></div>
          </div>

          {/* QR Code Container with rotation animation */}
          <div 
            id={activeTab === 'url' ? 'qr-popup-url' : 'qr-popup-contact'}
            className={`bg-white p-4 rounded-xl shadow-md border-2 border-gray-100 relative transition-transform duration-300 ${isRotating ? 'animate-qr-rotate' : ''}`}
            data-testid="qr-code-container"
          >
            {isGeneratingVCard && activeTab === 'contact' ? (
              <div className="w-64 h-64 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : currentQRValue ? (
              <>
                <QRCode
                  value={currentQRValue}
                  size={256}
                  level="H"
                  fgColor={activeTab === 'contact' ? '#6B7280' : qrColor}
                  bgColor="#FFFFFF"
                />
                {/* Logo overlay - show on both tabs */}
                {/* Premium cards: user's logo, Non-premium: Piper Card logo */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <img 
                    src={isPremium ? logoSrc : '/logo.png'} 
                    alt="Logo"
                    className="w-12 h-12 rounded-full bg-white border-2 border-white object-cover shadow-lg"
                    draggable="false"
                  />
                </div>
              </>
            ) : (
              <div className="w-64 h-64 flex items-center justify-center text-gray-400">
                <span>No QR data</span>
              </div>
            )}
          </div>

          {/* Single Share Button */}
          <div className="mt-6 w-full">
            <button
              onClick={handleShareQRImage}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
              data-testid="share-qr-btn"
            >
              <Share2 className="w-5 h-5" />
              <span>Share</span>
            </button>
          </div>

          {/* Swipe hint - mobile only */}
          <p className="sm:hidden text-xs text-gray-400 mt-3 text-center">
            Swipe left/right to switch
          </p>
        </div>
      </div>

      {/* CSS for slide-up animation */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
        @keyframes qr-rotate {
          0% {
            transform: rotateY(0deg);
          }
          50% {
            transform: rotateY(90deg);
          }
          100% {
            transform: rotateY(0deg);
          }
        }
        .animate-qr-rotate {
          animation: qr-rotate 0.4s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default QRPopupEnhanced;
